import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  MessageCircleQuestion,
  Mail,
  Mic,
  MessageSquare,
  Search,
  Download,
  LogOut,
  RefreshCw,
  Inbox,
} from "lucide-react";

const TABS = [
  { id: "questions", label: "Questions", icon: MessageCircleQuestion },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "speaking-requests", label: "Speaking Requests", icon: Mic },
  { id: "contact", label: "Contact", icon: MessageSquare },
];

const STATUS_OPTIONS = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "read", label: "Read" },
  { id: "responded", label: "Responded" },
  { id: "archived", label: "Archived" },
];

const statusBadge = (s) => {
  const colors = {
    new: "bg-sky-50 text-sky-700 border-sky-200",
    read: "bg-slate-50 text-slate-600 border-slate-200",
    responded: "bg-emerald-50 text-emerald-700 border-emerald-200",
    archived: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return colors[s] || colors.new;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout, checkAuth } = useAuth();
  const [tab, setTab] = useState("questions");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // If user data was passed via location state from AuthCallback, skip re-check
  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/admin/login", { replace: true });
    }
  }, [loading, user, location.state, navigate]);

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Not authorized for admin.");
      navigate("/admin/login", { replace: true, state: { unauthorized: true } });
    }
  }, [user, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        await checkAuth();
        navigate("/admin/login", { replace: true });
      }
    }
  }, [checkAuth, navigate]);

  const fetchRows = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get(`/admin/${tab}`, { params });
      setRows(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setBusy(false);
    }
  }, [tab, status, q, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchStats();
      fetchRows();
    }
  }, [user, tab, status, fetchStats, fetchRows]);

  // Debounced search
  useEffect(() => {
    if (!user?.is_admin) return;
    const t = setTimeout(() => fetchRows(), 350);
    return () => clearTimeout(t);
  }, [q, user, fetchRows]);

  const updateStatus = async (item, newStatus) => {
    try {
      await api.patch(`/admin/${tab}/${item.id}`, { status: newStatus });
      setRows((prev) =>
        prev.map((r) => (r.id === item.id ? { ...r, status: newStatus } : r))
      );
      fetchStats();
      toast.success(`Marked as ${newStatus}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/${tab}/export.csv`;
    window.open(url, "_blank");
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="h-10 w-10 border-2 border-[#0369A1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentTab = TABS.find((t) => t.id === tab);

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#0369A1] text-white font-heading">
              D
            </span>
            <div>
              <p className="font-heading text-slate-900 leading-none">Admin</p>
              <p className="text-[11px] text-slate-500 leading-none mt-1">
                Ask Chaplain Derrick
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || user.email}
                  className="h-8 w-8 rounded-full"
                />
              ) : (
                <span className="h-8 w-8 rounded-full bg-slate-100 inline-flex items-center justify-center text-slate-600 text-xs font-medium">
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="text-slate-700">{user.name || user.email}</span>
            </div>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await logout();
                navigate("/admin/login", { replace: true });
              }}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-full px-3 py-1.5"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
          {TABS.map((t) => {
            const Icon = t.icon;
            const key =
              t.id === "speaking-requests"
                ? "speaking_requests"
                : t.id === "contact"
                ? "contact_messages"
                : t.id;
            const total = stats?.[key]?.total ?? 0;
            const newCount = stats?.[key]?.new ?? 0;
            return (
              <button
                key={t.id}
                data-testid={`stat-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`text-left bg-white rounded-2xl border p-5 transition-colors ${
                  tab === t.id
                    ? "border-[#0369A1] shadow-sm"
                    : "border-slate-100 hover:border-slate-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="h-9 w-9 rounded-full bg-[#F0F9FF] text-[#0369A1] flex items-center justify-center">
                    <Icon size={16} />
                  </div>
                  {newCount > 0 && (
                    <span className="text-[10px] uppercase tracking-widest bg-amber-50 text-[#D97706] border border-amber-100 rounded-full px-2 py-0.5">
                      {newCount} new
                    </span>
                  )}
                </div>
                <p className="mt-4 text-xs text-slate-500">{t.label}</p>
                <p className="mt-1 font-heading text-2xl text-slate-900">{total}</p>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="mt-8 bg-white rounded-2xl border border-slate-100 p-4 sm:p-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            {currentTab && (
              <>
                <currentTab.icon size={18} className="text-[#0369A1]" />
                <h2 className="font-heading text-lg text-slate-900">
                  {currentTab.label}
                </h2>
              </>
            )}
            <span className="text-xs text-slate-400">{rows.length} shown</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                data-testid="admin-search"
                placeholder="Search..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8 pr-3 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none w-full sm:w-56"
              />
            </div>
            <select
              data-testid="admin-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="text-sm rounded-full border border-slate-200 bg-white px-3 py-2 focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  Status: {s.label}
                </option>
              ))}
            </select>
            <button
              data-testid="admin-refresh"
              onClick={() => {
                fetchRows();
                fetchStats();
              }}
              className="text-sm border border-slate-200 hover:border-[#0369A1] rounded-full px-3 py-2 inline-flex items-center gap-1.5 text-slate-700"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              className="text-sm bg-[#0369A1] hover:bg-[#0284C7] text-white rounded-full px-4 py-2 inline-flex items-center gap-1.5"
            >
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>

        {/* List */}
        <div className="mt-5 bg-white rounded-2xl border border-slate-100">
          {busy && (
            <div className="p-10 flex items-center justify-center text-slate-400 text-sm">
              <RefreshCw className="animate-spin mr-2" size={14} /> Loading...
            </div>
          )}
          {!busy && rows.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              <Inbox className="mx-auto mb-3 text-slate-300" size={28} />
              <p className="text-sm">No items to show.</p>
            </div>
          )}
          {!busy && rows.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="admin-rows">
              {rows.map((r) => (
                <li
                  key={r.id}
                  data-testid={`admin-row-${r.id}`}
                  className="p-5 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${statusBadge(
                            r.status || "new"
                          )}`}
                        >
                          {r.status || "new"}
                        </span>
                        {tab === "questions" && r.topic && (
                          <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-600">
                            {r.topic}
                          </span>
                        )}
                        {tab === "speaking-requests" && r.event_type && (
                          <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-600">
                            {r.event_type}
                          </span>
                        )}
                        <span className="text-[11px] text-slate-400">
                          {fmtDate(r.created_at)}
                        </span>
                      </div>

                      <p className="mt-2 font-heading text-slate-900 text-base">
                        {r.name || (r.anonymous ? "Anonymous" : r.email) || r.email || "—"}
                      </p>
                      {r.email && r.name && (
                        <p className="text-xs text-slate-500">{r.email}</p>
                      )}

                      <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">
                        {r.question || r.message || r.first_name || r.email || "—"}
                      </p>

                      <button
                        onClick={() =>
                          setExpandedId(expandedId === r.id ? null : r.id)
                        }
                        className="mt-2 text-xs text-[#0369A1] hover:text-[#0284C7]"
                      >
                        {expandedId === r.id ? "Hide details" : "View details"}
                      </button>

                      {expandedId === r.id && (
                        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm text-slate-700 space-y-1">
                          {Object.entries(r).map(([k, v]) => {
                            if (k === "id") return null;
                            if (v === null || v === undefined || v === "") return null;
                            const display = Array.isArray(v) ? v.join(", ") : String(v);
                            return (
                              <div key={k} className="flex gap-2">
                                <span className="text-slate-500 uppercase text-[10px] tracking-widest w-32 shrink-0">
                                  {k.replace(/_/g, " ")}
                                </span>
                                <span className="break-words">{display}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex sm:flex-col flex-wrap gap-2 sm:w-44 shrink-0">
                      {["new", "read", "responded", "archived"].map((s) => (
                        <button
                          key={s}
                          data-testid={`row-${r.id}-status-${s}`}
                          onClick={() => updateStatus(r, s)}
                          disabled={r.status === s}
                          className={`text-[11px] uppercase tracking-widest rounded-full px-3 py-1.5 border transition-colors ${
                            r.status === s
                              ? "bg-[#0369A1] text-white border-[#0369A1]"
                              : "bg-white text-slate-600 border-slate-200 hover:border-[#0369A1]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">
          Allow-listed admins only · Submissions stored securely · Real Questions. Honest Answers. Practical Hope.
        </p>
      </main>
    </div>
  );
};

export default AdminDashboard;
